import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSize } from 'ahooks';
import dayjs, { Dayjs } from 'dayjs';
import { cancelFullscreen, goFullscreen } from '@/pages/WgqAllMaps/utils';
import AreaMatrixSection from './components/AreaMatrixSection';
import DetailSection from './components/DetailSection';
import LevelSidebar from './components/LevelSidebar';
import MediumToolbar, { MEDIUMS } from './components/MediumToolbar';
import RealtimeLoadSection from './components/RealtimeLoadSection';
import ResilienceSummary from './components/ResilienceSummary';
import ScreenHeader from './components/ScreenHeader';
import RecalculateModal from './components/RecalculateModal';
import {
  getMockAreaMatrix,
  getMockAreaOverview,
  getMockRealtimeTrend,
  getMockResilienceDetail,
  MOCK_RESILIENCE_TREE,
} from './mockData';
import { getPeriodRange, resolveDimensionMeta } from './utils';
import styles from './index.module.scss';
import type {
  AnalysisGroup,
  AnalysisRow,
  AnalysisViewMode,
  Dimension,
  EnergyResilienceAnalysisTreeNode,
  PeriodMode,
} from './types';

const MAX_ROWS_PER_GROUP = 5;

const getMediumNode = (tree: EnergyResilienceAnalysisTreeNode[], mediumType: number) =>
  tree.find(node => Number(node.mediumType) === mediumType);

const getDimensionNodes = (mediumNode?: EnergyResilienceAnalysisTreeNode) => {
  if (!mediumNode) return [];
  if (mediumNode.dimension) return [mediumNode];
  return (mediumNode.children ?? []).filter(node => node.dimension);
};

const getDisplayNodes = (node: EnergyResilienceAnalysisTreeNode) => {
  if (!node.children?.length) return [node];
  return node.children.slice(0, MAX_ROWS_PER_GROUP);
};

const setFitScale = (target: HTMLDivElement | null, width?: number, height?: number, fullscreen?: boolean) => {
  if (!target || !width || !height) return;
  const designWidth = width > 2560 ? 2970 : 1920;
  const availableHeight = fullscreen
    ? document.documentElement.clientHeight
    : document.documentElement.clientHeight - 50;
  const scale = width / designWidth;

  if (width >= designWidth) {
    target.style.width = `${designWidth}px`;
    target.style.height = `${availableHeight / scale}px`;
    target.style.transform = `scale3d(${scale}, ${scale}, ${scale})`;
    target.style.transformOrigin = 'top left';
    return;
  }

  target.style.width = '';
  target.style.height = '';
  target.style.transform = '';
  target.style.transformOrigin = '';
};

const EnergyResilienceDashboard: React.FC = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const screenRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const size = useSize(pageRef);
  const [viewMode, setViewMode] = useState<AnalysisViewMode>('medium');
  const [mediumType, setMediumType] = useState(0);
  const [periodMode, setPeriodMode] = useState<PeriodMode>('year');
  const [date, setDate] = useState<Dayjs>(() => dayjs());
  const [keyword, setKeyword] = useState('');
  const [activeDimension, setActiveDimension] = useState<Dimension>();
  const [fullscreen, setFullscreen] = useState(false);
  const [recalculateOpen, setRecalculateOpen] = useState(false);
  const [mockRevision, setMockRevision] = useState(0);
  const periodRange = useMemo(() => getPeriodRange(periodMode, date), [date, periodMode]);

  useEffect(() => {
    const handleFullscreenChange = () => setFullscreen(document.fullscreenElement !== null);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const handleResize = () => setFitScale(screenRef.current, size?.width, size?.height, fullscreen);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [fullscreen, size?.height, size?.width]);

  const tree = MOCK_RESILIENCE_TREE;

  const dimensionNodes = useMemo(
    () => getDimensionNodes(getMediumNode(tree, mediumType) ?? tree[0]),
    [mediumType, tree]
  );

  const groups = useMemo(
    () =>
      dimensionNodes.map(dimensionNode => {
        const dimension = dimensionNode.dimension as Dimension;
        const meta = resolveDimensionMeta(dimension, mediumType);
        const rows = getDisplayNodes(dimensionNode).map((node, index) => {
          const baseRow: AnalysisRow = {
            key: `${dimension}-${node.id ?? index}`,
            configId: node.id,
            name: node.name ?? '--',
            dimension,
          };
          const detail = getMockResilienceDetail(node, mediumType, periodRange, mockRevision);
          return { ...baseRow, ...detail, name: detail.name ?? baseRow.name };
        });

        return {
          key: dimension,
          name: meta.name,
          description: meta.description,
          icon: meta.icon,
          count: dimensionNode.count ?? rows.length,
          rows,
        } as AnalysisGroup;
      }),
    [dimensionNodes, mediumType, mockRevision, periodRange]
  );

  useEffect(() => {
    if (!activeDimension && groups[0]) setActiveDimension(groups[0].key);
    if (activeDimension && groups.length && !groups.some(group => group.key === activeDimension)) {
      setActiveDimension(groups[0].key);
    }
  }, [activeDimension, groups]);

  const mediumCounts = useMemo(() => {
    const counts = new Map<number, number>();
    tree.forEach(node => {
      const count = node.children?.length ?? 0;
      if (node.mediumType !== undefined) counts.set(Number(node.mediumType), count);
    });
    return counts;
  }, [tree]);

  const filteredGroups = useMemo(() => {
    const value = keyword.trim().toLowerCase();
    if (!value) return groups;
    return groups
      .map(group => ({ ...group, rows: group.rows.filter(row => row.name.toLowerCase().includes(value)) }))
      .filter(group => group.rows.length);
  }, [groups, keyword]);

  const realtimeTrend = useMemo(() => getMockRealtimeTrend(mediumType, mockRevision), [mediumType, mockRevision]);
  const areaOverview = useMemo(() => getMockAreaOverview(mockRevision), [mockRevision]);
  const areaRows = useMemo(() => getMockAreaMatrix(mockRevision), [mockRevision]);
  const showRealtimeLoad = mediumType !== 0 && periodMode === 'day' && activeDimension === 'plant';

  const handleDimensionChange = (dimension: Dimension) => {
    setActiveDimension(dimension);
    const section = detailRef.current?.querySelector(`[data-dimension="${dimension}"]`);
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleToggleFullscreen = () => {
    if (!pageRef.current) return;
    if (fullscreen) cancelFullscreen();
    else goFullscreen(pageRef.current);
  };

  const mediumName = MEDIUMS.find(item => item.value === mediumType)?.label ?? '--';
  const sectionName = viewMode === 'area' ? '生产区域供能负载时长矩阵' : `${mediumName}负载率分层统计`;
  const sectionDescription =
    viewMode === 'area'
      ? '按区域横向对比天然气、压缩空气、氧气、二氧化碳、氩气，不含电力'
      : '查看各层级统计对象在三个负载区间的累计持续时长';

  return (
    <div id="energy_resilience_dashboard_fullscreen" className={styles.page} ref={pageRef}>
      <div className={styles.screen} ref={screenRef}>
        <ScreenHeader
          fullscreen={fullscreen}
          onRecalculate={() => setRecalculateOpen(true)}
          onToggleFullscreen={handleToggleFullscreen}
        />
        <MediumToolbar
          viewMode={viewMode}
          mediumType={mediumType}
          periodMode={periodMode}
          date={date}
          counts={mediumCounts}
          onViewModeChange={setViewMode}
          onMediumChange={setMediumType}
          onPeriodModeChange={setPeriodMode}
          onDateChange={setDate}
        />
        <div className={styles.main}>
          <div className={styles.panel}>
            <div className={styles.sectionTitle}>
              <span className={styles.sectionName}>{sectionName}</span>
              <span className={styles.sectionDescription}>{sectionDescription}</span>
            </div>
            {viewMode === 'area' ? (
              <div className={styles.areaContent}>
                <AreaMatrixSection overview={areaOverview} rows={areaRows} />
              </div>
            ) : (
              <div className={styles.workspace}>
                <LevelSidebar groups={groups} activeDimension={activeDimension} onChange={handleDimensionChange} />
                <div className={styles.content}>
                  <ResilienceSummary groups={groups} periodMode={periodMode} />
                  {showRealtimeLoad ? (
                    <RealtimeLoadSection mediumName={mediumName} groups={groups} trend={realtimeTrend} />
                  ) : (
                    <DetailSection
                      ref={detailRef}
                      mediumType={mediumType}
                      mediumName={mediumName}
                      groups={filteredGroups}
                      keyword={keyword}
                      onKeywordChange={setKeyword}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <RecalculateModal
        open={recalculateOpen}
        onCancel={() => setRecalculateOpen(false)}
        onSuccess={() => setMockRevision(value => value + 1)}
      />
    </div>
  );
};

export default EnergyResilienceDashboard;
